import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import LizSalesChat from './LizSalesChat';
import { useCnpjLookup } from '../hooks/useCnpjLookup';
import { useCepLookup } from '../hooks/useCepLookup';
import CrecheCadastroFields, { CrecheCadastroData, CRECHE_CADASTRO_VAZIO } from '../shared/CrecheCadastroFields';

/* ── Dados estáticos ───────────────────────────────────── */

const STATS = [
  { valor: '300+',   label: 'Creches protegidas' },
  { valor: '18.000+', label: 'Crianças sob vigilância segura' },
  { valor: '98%',     label: 'Confiança dos diretores' },
  { valor: '24/7',    label: 'Liz IA atendendo pais' },
];

const FEATURES = [
  {
    icon: '🔒',
    titulo: 'Segurança Garantida',
    desc: 'Controle de acesso, vigilância centralizada, LGPD rigorosa. Cada entrada, saída e ação é registrada e auditada para máxima tranquilidade.',
    destaque: 'Auditoria · LGPD · Logs',
    cor: 'lp-card-security',
  },
  {
    icon: '🤖',
    titulo: 'Assistente Liz — IA Real',
    desc: 'Atende pais 24h via chat ou voz ao vivo. Respostas sobre horários, saúde, desenvolvimento. Sua creche fala com inteligência.',
    destaque: 'Voz ao Vivo · Grounded · IA Real',
    cor: 'lp-card-ia',
  },
  {
    icon: '⚡',
    titulo: 'Notificações em Tempo Real',
    desc: 'Avisos instantâneos de eventos importantes: chegadas, saídas, alertas pedagógicos, comunicados. Pais nunca ficam desinformados.',
    destaque: 'Socket.IO · Badges · Instantâneo',
    cor: 'lp-card-ws',
  },
  {
    icon: '📱',
    titulo: 'PWA — App Nativo no Celular',
    desc: 'Painel para gestores e portal para pais funcionam como apps nativos. Offline, push notifications, segurança de ponta a ponta.',
    destaque: 'Offline · Push · Nativo',
    cor: 'lp-card-pwa',
  },
  {
    icon: '🏢',
    titulo: 'Plug & Play — Implantação em 1 Dia',
    desc: 'Seu contrato é gerado automaticamente, sua instância é criada em minutos. Começa a operar com segurança total no mesmo dia.',
    destaque: 'Automático · Instantâneo · LGPD',
    cor: 'lp-card-mt',
  },
  {
    icon: '💰',
    titulo: 'Gestão Financeira Completa',
    desc: 'Mensalidades, cobranças automáticas, fluxo de caixa. Sistema integrado que economiza tempo e reduz inadimplência.',
    destaque: 'Automação · Integrado · Controle',
    cor: 'lp-card-fin',
  },
];

const MODULOS = [
  { 
    icon: '🏢', 
    label: 'Administrativo',
    desc: 'Controle central da unidade: documentos da creche, alvarás, vistorias prediais e alertas automáticos de vencimento.',
    planos: ['Bronze', 'Prata', 'Ouro']
  },
  { 
    icon: '🧠', 
    label: 'Liz AI Assistant',
    desc: 'Sua assistente inteligente para suporte a pais, geração de relatórios e automação de FAQ via texto e voz em tempo real.',
    planos: ['Bronze', 'Prata', 'Ouro']
  },
  { 
    icon: '🌟', 
    label: 'AEE (Especial)',
    desc: 'Atendimento Educacional Especializado: PDI, registros de inclusão, terapias externas e acompanhamento multidisciplinar.',
    planos: ['Prata', 'Ouro']
  },
  { 
    icon: '📅', 
    label: 'Calendário',
    desc: 'Eventos, reuniões de pais, feriados e cronograma escolar compartilhado com notificações automáticas.',
    planos: ['Bronze', 'Prata', 'Ouro']
  },
  { 
    icon: '🍱', 
    label: 'Nutrição',
    desc: 'Gestão de cardápios semanais, controle de estoque de despensa e registro de aceitação alimentar diário.',
    planos: ['Bronze', 'Prata', 'Ouro']
  },
  { 
    icon: '💬', 
    label: 'Comunicação',
    desc: 'Mural de avisos, chat em tempo real, enquetes interativas e autorizações digitais com protocolo de leitura.',
    planos: ['Bronze', 'Prata', 'Ouro']
  },
  { 
    icon: '💰', 
    label: 'Financeiro',
    desc: 'Gestão de mensalidades, geração de boletos/Pix, controle de fluxo de caixa e faturamento SaaS automatizado.',
    planos: ['Bronze', 'Prata', 'Ouro']
  },
  { 
    icon: '👶', 
    label: 'Crianças',
    desc: 'Perfil 360: fotos, ficha médica avançada (SOS/Crises), medicamentos e histórico pedagógico unificado.',
    planos: ['Bronze', 'Prata', 'Ouro']
  },
  { 
    icon: '👨‍👩‍👧', 
    label: 'Famílias',
    desc: 'Vínculos de responsáveis, controle de quem pode buscar e portal exclusivo para pais via PWA.',
    planos: ['Bronze', 'Prata', 'Ouro']
  },
  { 
    icon: '🧑‍🏫', 
    label: 'Funcionários',
    desc: 'Registro de ponto, treinamentos, avaliações de desempenho e portal do colaborador para consulta de escalas.',
    planos: ['Bronze', 'Prata', 'Ouro']
  },
  { 
    icon: '🏗️', 
    label: 'Infraestrutura',
    desc: 'Chamados de manutenção, vistorias prediais e gestão de patrimônio da unidade escolar.',
    planos: ['Ouro']
  },
  { 
    icon: '🔒', 
    label: 'Segurança & LGPD',
    desc: 'Monitoramento, controle de acesso e conformidade total com privacidade de dados sensíveis.',
    planos: ['Bronze', 'Prata', 'Ouro']
  },
  { 
    icon: '📚', 
    label: 'Pedagógico',
    desc: 'Planos de aula alinhados à BNCC, portfólios digitais e relatórios semestrais de desenvolvimento.',
    planos: ['Prata', 'Ouro']
  },
  { 
    icon: '📊', 
    label: 'Relatórios/BI',
    desc: 'Análise de dados avançada: taxas de ocupação, evasão e saúde financeira em tempo real.',
    planos: ['Prata', 'Ouro']
  },
  {
    icon: '📋',
    label: 'Turmas & Matrículas',
    desc: 'Organização por faixa etária, limites de ocupação, enturmação inteligente e gestão de lista de espera.',
    planos: ['Bronze', 'Prata', 'Ouro']
  },
];

const STEPS = [
  {
    numero: '01',
    titulo: 'Escolha seu Plano com Segurança Garantida',
    desc: 'Selecione Bronze, Prata ou Ouro. Seu contrato digital é gerado automaticamente com total conformidade LGPD e cláusulas de proteção de dados.',
  },
  {
    numero: '02',
    titulo: 'Seu Ambiente Seguro em 1 Dia',
    desc: 'Sua instância é provisionada com isolamento total de dados. Começamos com base vazia, segura, pronta para dados sensíveis de crianças e famílias.',
  },
  {
    numero: '03',
    titulo: 'Implantação com Suporte Dedicado',
    desc: 'Nossa equipe auxilia na importação de dados com segurança. Gestores, equipe e famílias conectados com vigilância integrada e notificações em tempo real.',
  },
  {
    numero: '04',
    titulo: 'Fale com a Liz — Sua IA 24h',
    desc: 'Liz responde dúvidas sobre o sistema, segurança, LGPD e funcionalidades. Ela está aqui para tranquilizar você e sua equipe desde o primeiro dia.',
    cta: 'Converse com a Liz',
  },
];

/**
 * ⚖️ Os planos NÃO moram mais aqui.
 *
 * Nome, preço e itens vêm de `GET /saas/planos-publicos`, que lê o catálogo
 * oficial do servidor. Enquanto esta página tinha a própria cópia, ela divergiu
 * do sistema: o site vendia OURO a R$ 597 e o banco guardava um "Enterprise"
 * gratuito e desligado, e o preço cobrado era montado aqui no navegador.
 *
 * O que fica no frontend é só aparência — a cor do card não é informação
 * comercial.
 */
const CORES_POR_PLANO: Record<string, string> = {
  BRONZE: 'lp-plan-basic',
  PRATA: 'lp-plan-pro',
  OURO: 'lp-plan-rede',
};

interface PlanoPublico {
  chave: string;
  nome: string;
  preco_mensal: number;
  descricao: string;
  destaque: boolean;
  itens: { texto: string; disponivel: boolean }[];
}

const DEPOIMENTOS = [
  {
    nome: 'Cliente verificado',
    cargo: 'Rede de ensino',
    texto: 'Estamos substituindo referencias por relatos reais com consentimento expresso, mantendo privacidade e conformidade.',
    avatar: 'V',
    cor: '#7c3aed',
  },
  {
    nome: 'Cliente verificado',
    cargo: 'Coordenacao pedagogica',
    texto: 'Em breve, a secao exibira depoimentos validados com identificacao publica autorizada por cada cliente.',
    avatar: 'V',
    cor: '#059669',
  },
];

const FAQ = [
  {
    p: 'Preciso instalar algum software no servidor?',
    r: 'Não. O sistema é 100% na nuvem — você acessa pelo navegador e as famílias instalam o PWA direto do celular, sem app store.',
  },
  {
    p: 'Quantas creches posso cadastrar no mesmo contrato?',
    r: 'No plano Essencial e Profissional, cada unidade é uma assinatura separada. Para redes (3+ unidades), o plano Rede oferece desconto consolidado e painel centralizado.',
  },
  {
    p: 'A IA precisa de configuração técnica?',
    r: 'Não. Você define o nome, tom e tópicos autorizados num painel intuitivo. Os provedores de LLM (Gemini, Groq, OpenAI) são configurados pela Alencastro Consultoria na contratação.',
  },
  {
    p: 'O sistema cumpre a LGPD?',
    r: 'Sim. Inclui termos de uso versionados, consentimento no primeiro acesso, log de acessos e suporte ao direito de exclusão de dados, atendendo à Lei nº 13.709/2018.',
  },
  {
    p: 'Como funciona o suporte?',
    r: 'Plano Essencial: suporte por e-mail em até 48h. Plano Profissional: suporte prioritário por chat em até 4h. Plano Rede: gerente de sucesso dedicado.',
  },
];

/* ── Componente principal ──────────────────────────────── */

export default function LandingPage() {
  const [lizChatOpen, setLizChatOpen] = useState(false);
  const [faqAberta, setFaqAberta] = useState<number | null>(null);
  const [visivel, setVisivel] = useState<Set<string>>(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);

  const [hoveredModId, setHoveredModId] = useState<number | null>(null);

  // Estados para o Checkout/Lead
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [checkoutData, setCheckoutData] = useState<CrecheCadastroData>(CRECHE_CADASTRO_VAZIO);

  // Os planos vêm do servidor — ver CORES_POR_PLANO acima. Sem cópia local:
  // se a API não responder, a página NÃO inventa preço nenhum.
  const [planos, setPlanos] = useState<PlanoPublico[]>([]);
  const [planosErro, setPlanosErro] = useState(false);

  useEffect(() => {
    let ativo = true;
    api
      .get('/saas/planos-publicos')
      .then((r) => { if (ativo) { setPlanos(r.data || []); setPlanosErro(false); } })
      .catch(() => { if (ativo) setPlanosErro(true); });
    return () => { ativo = false; };
  }, []);
  const { buscar: buscarCnpj, buscando: buscandoCnpjLead, erro: erroCnpjLead } = useCnpjLookup();
  const { buscar: buscarCep, buscando: buscandoCepLead } = useCepLookup();

  async function buscarCnpjLead() {
    const resultado = await buscarCnpj(checkoutData.cnpj);
    if (!resultado) return;
    setCheckoutData(prev => ({
      ...prev,
      razaoSocial: resultado.razaoSocial || prev.razaoSocial,
      nomeFantasia: resultado.nomeFantasia || prev.nomeFantasia,
      endereco: resultado.endereco || prev.endereco,
      numero: resultado.numero || prev.numero,
      complemento: resultado.complemento || prev.complemento,
      bairro: resultado.bairro || prev.bairro,
      cidade: resultado.cidade || prev.cidade,
      estado: resultado.estado || prev.estado,
      cep: resultado.cep || prev.cep,
      telefone: resultado.telefone || prev.telefone,
    }));
  }

  async function buscarCepLead() {
    const resultado = await buscarCep(checkoutData.cep);
    if (!resultado) return;
    setCheckoutData(prev => ({
      ...prev,
      endereco: resultado.endereco || prev.endereco,
      bairro: resultado.bairro || prev.bairro,
      cidade: resultado.cidade || prev.cidade,
      estado: resultado.estado || prev.estado,
    }));
  }

  const handleModEnter = (id: number) => {
    setHoveredModId(id);
  };

  const handleModLeave = () => {
    setHoveredModId(null);
  };

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.target.id) {
            setVisivel((prev) => new Set(Array.from(prev).concat(entry.target.id)));
          }
        });
      },
      { threshold: 0.12 },
    );

    document.querySelectorAll('[data-animate]').forEach((el) => {
      observerRef.current!.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, []);

  function animClass(id: string) {
    return visivel.has(id) ? 'lp-animate lp-animate--in' : 'lp-animate';
  }

  return (
    <div className="lp-root">

      {/* ══════════════ HEADER ══════════════ */}
      <header className="lp-header">
        <div className="lp-header-inner">
          <a href="#inicio" className="lp-logo">
            <span className="lp-logo-icon">🏫</span>
            <span className="lp-logo-text">Gestão<strong>Creches</strong></span>
          </a>
          <nav className="lp-nav">
            <a href="#funcionalidades" className="lp-nav-link">Funcionalidades</a>
            <a href="#modulos" className="lp-nav-link">Módulos</a>
            <a href="#como-funciona" className="lp-nav-link">Como funciona</a>
            <a href="#planos" className="lp-nav-link">Planos</a>
            <a href="#contato" className="lp-nav-link">Contato</a>
          </nav>
          <div className="lp-header-actions">
            <Link to="/saas/login" className="lp-nav-link" style={{ fontSize: '14px', marginRight: '10px' }}>Portal Admin SaaS</Link>
            <a href="#planos" className="lp-btn-demo">Solicitar demo</a>
            <Link to="/login" className="lp-btn-login">Acessar →</Link>
          </div>
        </div>
      </header>

      {/* ══════════════ HERO ══════════════ */}
      <section className="lp-hero" id="inicio">
        <div className="lp-hero-bg">
          <div className="lp-hero-orb lp-hero-orb-1" />
          <div className="lp-hero-orb lp-hero-orb-2" />
          <div className="lp-hero-orb lp-hero-orb-3" />
          <div className="lp-hero-grid" />
        </div>

        <div className="lp-hero-inner">
          <div className="lp-hero-content">
            <div className="lp-hero-badge">
              <span className="lp-badge-dot" />
              Sistema SaaS · Multi-tenant · IA integrada
            </div>

            <h1 className="lp-hero-title">
              A <span className="lp-hero-accent">Segurança</span><br />
              que sua Creche Merecia
            </h1>

            <p className="lp-hero-subtitle">
              Gestão completa com LGPD, vigilância integrada, Liz AI 24h, notificações em tempo real
              e 15 módulos — tudo para dar tranquilidade aos pais e controle aos diretores.
            </p>

            <div className="lp-hero-ctas">
              <a href="#planos" className="lp-cta-primary">
                <span>Solicitar demonstração</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </a>
              <a href="#funcionalidades" className="lp-cta-secondary">
                Ver funcionalidades
              </a>
            </div>

            <div className="lp-hero-trust">
              <span className="lp-trust-item">✓ Sem cartão de crédito</span>
              <span className="lp-trust-sep">·</span>
              <span className="lp-trust-item">✓ Setup em 1 dia</span>
              <span className="lp-trust-sep">·</span>
              <span className="lp-trust-item">✓ Suporte incluso</span>
            </div>
          </div>

          {/* Dashboard mockup */}
          <div className="lp-hero-visual">
            <div className="lp-mockup">
              <div className="lp-mockup-bar">
                <span /><span /><span />
              </div>
              <div className="lp-mockup-body">
                <div className="lp-mockup-sidebar">
                  {['📊','👶','💬','💰','🤖','📋'].map((ic, i) => (
                    <div key={i} className="lp-mockup-nav-item">{ic}</div>
                  ))}
                </div>
                <div className="lp-mockup-content">
                  <div className="lp-mock-row">
                    {['👶 248 alunos','💰 R$48k/mês','📋 Rotinas ok'].map((t, i) => (
                      <div key={i} className="lp-mock-stat">{t}</div>
                    ))}
                  </div>
                  <div className="lp-mock-chat">
                    <div className="lp-mock-bubble lp-mock-bot">
                      <strong>Lia</strong>
                      <span>Bom dia! Temos 3 alertas hoje. Deseja ver os detalhes?</span>
                    </div>
                    <div className="lp-mock-bubble lp-mock-user">
                      <span>Sim, mostre o financeiro</span>
                    </div>
                    <div className="lp-mock-bubble lp-mock-bot lp-mock-typing">
                      <span/><span/><span/>
                    </div>
                  </div>
                  <div className="lp-mock-notif">
                    <span className="lp-mock-notif-dot" />
                    <span>2 novas mensagens das famílias</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating badges */}
            <div className="lp-float lp-float-1">
              <span>🔔</span> Notificação em tempo real
            </div>
            <div className="lp-float lp-float-2">
              <span>🤖</span> Assistente Liz AI — online
            </div>
            <div className="lp-float lp-float-3">
              <span>📱</span> PWA instalado
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <div className="lp-stats-bar">
          {STATS.map((s) => (
            <div key={s.label} className="lp-stat">
              <strong>{s.valor}</strong>
              <span>{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════ FEATURES ══════════════ */}
      <section className="lp-section lp-section-light" id="funcionalidades">
        <div className="lp-container">
          <div
            className={animClass('feat-head')}
            id="feat-head"
            data-animate="true"
          >
            <h2 className="lp-section-title">Gestão Segura e Inteligente</h2>
            <p className="lp-section-kicker">Segurança que não compromete</p>
            <p className="lp-section-sub">
              Desenvolvido por quem entende os riscos reais de creches e CEIs. Cada funcionalidade protege,
              automatiza e tranquiliza — sem complicações, sem vulnerabilidades, sem comprometimentos.
            </p>
          </div>

          <div className="lp-features-grid">
            {FEATURES.map((f, i) => (
              <div
                key={f.titulo}
                className={`lp-feature-card ${f.cor} ${animClass(`feat-${i}`)}`}
                id={`feat-${i}`}
                data-animate="true"
                style={{ transitionDelay: `${i * 0.07}s` }}
              >
                <div className="lp-feature-icon">{f.icon}</div>
                <h3>{f.titulo}</h3>
                <p>{f.desc}</p>
                <span className="lp-feature-tag">{f.destaque}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════ IA SHOWCASE ══════════════ */}
      <section className="lp-section lp-section-dark" id="ia">
        <div className="lp-container">
          <div className="lp-ia-grid">
            <div
              className={animClass('ia-content')}
              id="ia-content"
              data-animate="true"
            >
              <p className="lp-section-kicker lp-kicker-light">IA Integrada à Segurança</p>
              <h2 className="lp-section-title lp-title-light">
                Conheça a Liz,<br />seu assistente 24h<br />de confiança
              </h2>
              <p className="lp-section-sub lp-sub-light">
                Liz atende pais 24 horas via texto ou voz ao vivo. Respostas seguras sobre horários, saúde,
                desenvolvimento e comportamento. Para você, análises de segurança, alertas de crianças e
                insights pedagógicos — tudo com dados protegidos e LGPD garantida.
              </p>

              <ul className="lp-ia-list">
                <li>
                  <span className="lp-ia-check">✓</span>
                  <div>
                    <strong>Voz ao vivo com Gemini Live</strong>
                    <span>Conversa de voz em tempo real via WebSocket, sem latência.</span>
                  </div>
                </li>
                <li>
                  <span className="lp-ia-check">✓</span>
                  <div>
                    <strong>Fallback inteligente</strong>
                    <span>Se Gemini falhar, usa Groq; se Groq falhar, usa OpenAI. Sempre responde.</span>
                  </div>
                </li>
                <li>
                  <span className="lp-ia-check">✓</span>
                  <div>
                    <strong>Grounding com seus dados</strong>
                    <span>Respostas baseadas no cardápio real, horários e materiais aprovados pela gestão.</span>
                  </div>
                </li>
                <li>
                  <span className="lp-ia-check">✓</span>
                  <div>
                    <strong>Insights e alertas automáticos</strong>
                    <span>Detecta padrões de frequência, humor, alimentação e alerta a equipe pedagógica.</span>
                  </div>
                </li>
              </ul>

              <a href="#planos" className="lp-cta-primary lp-cta-sm">
                Quero o assistente Liz AI
              </a>
            </div>

            <div
              className={`lp-ia-demo ${animClass('ia-demo')}`}
              id="ia-demo"
              data-animate="true"
            >
              <div className="lp-chat-demo">
                <div className="lp-chat-demo-header">
                  <div className="lp-chat-avatar">L</div>
                  <div>
                    <strong>Liz</strong>
                    <span>Assistente da creche · online</span>
                  </div>
                  <span className="lp-chat-online-dot" />
                </div>
                <div className="lp-chat-demo-msgs">
                  <div className="lp-chat-msg lp-chat-out">
                    Boa tarde! Pode me dizer o cardápio desta semana?
                  </div>
                  <div className="lp-chat-msg lp-chat-in">
                    Boa tarde! Aqui está o cardápio desta semana:<br />
                    <strong>Seg:</strong> Frango grelhado, arroz, feijão e salada<br />
                    <strong>Ter:</strong> Macarrão ao molho de tomate e frango<br />
                    <strong>Qua:</strong> Carne moída com legumes e purê<br />
                    Precisa de mais alguma informação?
                  </div>
                  <div className="lp-chat-msg lp-chat-out">
                    E qual o horário de entrada?
                  </div>
                  <div className="lp-chat-msg lp-chat-in">
                    A creche funciona de segunda a sexta, das 7h às 18h. A entrada é das 7h às 9h.
                  </div>
                </div>
                <div className="lp-chat-demo-input">
                  <span>Fale com a Liz…</span>
                    <button aria-label="Acionar assistente de voz" title="Acionar assistente de voz">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/></svg>
                  </button>
                </div>
              </div>

              <div className="lp-ia-providers">
                <span>Powered by</span>
                <div className="lp-ia-provider-badges">
                  <span className="lp-provider-badge lp-provider-gemini">Gemini</span>
                  <span className="lp-provider-badge lp-provider-groq">Groq</span>
                  <span className="lp-provider-badge lp-provider-openai">OpenAI</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════ MÓDULOS ══════════════ */}
      <section className="lp-section lp-section-modulos" id="modulos">
        <div className="lp-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
          <div className="lp-modulos-header" style={{ width: '100%', textAlign: 'center', marginBottom: '60px' }}>
            <p className="lp-section-kicker lp-kicker-light">Cobertura total e segura</p>
            <h2 className="lp-section-title lp-title-light">15 Módulos com Vigilância Integrada</h2>
            <p className="lp-section-sub lp-sub-light">
              Do perfil de cada criança ao histórico de acessos, vigilância de entrada/saída, RH controlado,
              financeiro transparente e conformidade LGPD completa. Uma plataforma que protege, controla e tranquiliza.
            </p>
          </div>

          <div className="lp-modulos-grid" style={{ width: '100%', position: 'relative' }}>
            {MODULOS.map((m, i) => (
              <div
                key={m.label}
                className={`lp-modulo-item-wrapper ${animClass(`mod-${i}`)}`}
                id={`mod-${i}`}
                data-animate="true"
                style={{ transitionDelay: `${i * 0.04}s` }}
                onMouseEnter={() => handleModEnter(i)}
                onMouseLeave={handleModLeave}
              >
                <div className="lp-modulo-item">
                  <div className="lp-modulo-icon">{m.icon}</div>
                  <div className="lp-modulo-text">{m.label}</div>
                </div>

                {/* Popup Interno ao Item (Mais Robusto) */}
                {hoveredModId === i && (
                  <div className="lp-mod-popup">
                    <div className="lp-mod-popup-content">
                      <div className="lp-mod-popup-header">
                        <span className="lp-mod-popup-icon">{m.icon}</span>
                        <h3>{m.label}</h3>
                      </div>
                      <p className="lp-mod-popup-desc">{m.desc}</p>
                      <div className="lp-mod-popup-plans">
                        <span className="lp-mod-popup-plans-label">Disponível em:</span>
                        <div className="lp-mod-popup-plans-list">
                          {['Bronze', 'Prata', 'Ouro'].map(p => (
                            <span 
                              key={p} 
                              className={`lp-mod-plan-badge ${m.planos.includes(p) ? 'active' : ''}`}
                            >
                              {p}
                            </span>
                          ))}
                        </div>
                      </div>
                      {m.planos.length === 3 && (
                        <div className="lp-mod-plan-full">Incluso em todos os planos</div>
                      )}
                    </div>
                    <div className="lp-mod-popup-arrow"></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════ COMO FUNCIONA ══════════════ */}
      <section className="lp-section lp-section-gradient" id="como-funciona">
        <div className="lp-container">
          <div
            className={animClass('steps-head')}
            id="steps-head"
            data-animate="true"
          >
            <p className="lp-section-kicker lp-kicker-light">Simples e rápido</p>
            <h2 className="lp-section-title lp-title-light">Em 4 passos você está no ar</h2>
          </div>

          <div className="lp-steps">
            {STEPS.map((s, i) => (
              <div
                key={s.numero}
                className={`lp-step ${animClass(`step-${i}`)}`}
                id={`step-${i}`}
                data-animate="true"
                style={{ transitionDelay: `${i * 0.12}s` }}
              >
                <div className="lp-step-number">{s.numero}</div>
                {i < STEPS.length - 1 && <div className="lp-step-line" />}
                <h3>{s.titulo}</h3>
                <p>{s.desc}</p>
                {(s as any).cta && (
                  <button
                    type="button"
                    className="lp-cta-primary lp-cta-sm"
                    style={{ marginTop: 14, border: 'none', cursor: 'pointer' }}
                    onClick={() => setLizChatOpen(true)}
                  >
                    💬 {(s as any).cta}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="lp-wave-divider">
          <svg viewBox="0 0 1440 64" preserveAspectRatio="none">
            <path d="M0,64 L1440,64 L1440,0 C1120,64 320,64 0,0 Z" fill="#f8fafc" />
          </svg>
        </div>
      </section>

      {/* ══════════════ PLANOS ══════════════ */}
      <section className="lp-section lp-section-gradient" id="planos">
        <div className="lp-container lp-planos-wrap">
          <div
            className={animClass('planos-head')}
            id="planos-head"
            data-animate="true"
            style={{ paddingTop: '40px' }}
          >
            <p className="lp-section-kicker" style={{ color: '#7dd3fc' }}>Investimento</p>
            <h2 className="lp-section-title" style={{ color: '#ffffff' }}>Escolha o plano ideal</h2>
            <p className="lp-section-sub lp-sub-light">
              Todos os planos incluem LGPD, PWA, notificações em tempo real e suporte técnico.
              Sem taxas de implantação nos planos mensais.
            </p>
          </div>

          <div className="lp-planos-grid">
            {planos.map((p, i) => (
              <div
                key={p.chave}
                className={`lp-plano ${CORES_POR_PLANO[p.chave] || ''} ${p.destaque ? 'lp-plano-destaque' : ''} ${animClass(`plano-${i}`)}`}
                id={`plano-${i}`}
                data-animate="true"
                style={{ transitionDelay: `${i * 0.1}s` }}
              >
                {p.destaque && <div className="lp-plano-ribbon">Mais popular</div>}

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: p.destaque ? '16px' : '0', marginBottom: '8px' }}>
                  <h3 className="lp-plano-nome">{p.nome}</h3>
                  <span style={{ color: '#0284c7', fontSize: '20px', fontWeight: 'bold' }}>✓</span>
                </div>

                <div className="lp-plano-preco">
                  <strong>R$ {p.preco_mensal}</strong>
                  <span>/mês por unidade</span>
                </div>
                <ul className="lp-plano-items">
                  {p.itens.map((item) => (
                    <li key={item.texto}>
                      <span className="lp-plano-check">✓</span>
                      {item.texto}
                    </li>
                  ))}
                </ul>
                <a
                  href="#contato"
                  className={`lp-plano-btn ${p.destaque ? 'lp-plano-btn-destaque' : ''}`}
                  onClick={() => setCheckoutData(prev => ({ ...prev, plano: p.chave }))}
                >
                  Começar agora
                </a>
              </div>
            ))}

            {/* Sem os planos do servidor não se mostra preço nenhum: número
                inventado numa página de vendas é pior que número ausente. */}
            {planos.length === 0 && (
              <p style={{ gridColumn: '1 / -1', textAlign: 'center', opacity: 0.85 }}>
                {planosErro
                  ? <>Não consegui carregar os planos agora. Fale com <a href="mailto:comercial@gestaocreche.com.br">comercial@gestaocreche.com.br</a> que passamos os valores na hora.</>
                  : 'Carregando os planos…'}
              </p>
            )}
          </div>

          <p className="lp-planos-nota">
            * Preços em BRL. Mensalidade por unidade.
            Entre em contato para condições especiais para redes municipais e filantrópicas.
          </p>
        </div>
      </section>

      {/* ══════════════ DEPOIMENTOS ══════════════ */}
      <section className="lp-section lp-section-dark">
        <div className="lp-container">
          <div
            className={animClass('dep-head')}
            id="dep-head"
            data-animate="true"
          >
            <p className="lp-section-kicker lp-kicker-light">O que dizem os clientes</p>
            <h2 className="lp-section-title lp-title-light">Quem usa, recomenda</h2>
          </div>

          <div className="lp-depoimentos-grid">
            {DEPOIMENTOS.map((d, i) => (
              <div
                key={d.nome}
                className={`lp-depoimento ${animClass(`dep-${i}`)}`}
                id={`dep-${i}`}
                data-animate="true"
                style={{ transitionDelay: `${i * 0.1}s` }}
              >
                <p className="lp-dep-texto">"{d.texto}"</p>
                <div className="lp-dep-autor">
                  <div className="lp-dep-avatar" style={{ background: d.cor }}>{d.avatar}</div>
                  <div>
                    <strong>{d.nome}</strong>
                    <span>{d.cargo}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════ FAQ ══════════════ */}
      <section className="lp-section lp-section-light">
        <div className="lp-container lp-container-narrow">
          <div
            className={animClass('faq-head')}
            id="faq-head"
            data-animate="true"
          >
            <p className="lp-section-kicker">Dúvidas frequentes</p>
            <h2 className="lp-section-title">Perguntas e respostas</h2>
          </div>

          <div className="lp-faq">
            {FAQ.map((item, i) => (
              <div
                key={i}
                className={`lp-faq-item ${faqAberta === i ? 'lp-faq-open' : ''}`}
                onClick={() => setFaqAberta(faqAberta === i ? null : i)}
              >
                <div className="lp-faq-pergunta">
                  <span>{item.p}</span>
                  <span className="lp-faq-icon">{faqAberta === i ? '−' : '+'}</span>
                </div>
                {faqAberta === i && (
                  <div className="lp-faq-resposta">{item.r}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════ FORMULÁRIO DE CHECKOUT (LEAD) ══════════════ */}
      <section className="lp-section lp-section-cta" id="contato">
        <div className="lp-container">
          <div
            className={`lp-cta-block ${animClass('cta-block')}`}
            id="cta-block"
            data-animate="true"
          >
            <div className="lp-checkout-form-container">
              {!checkoutSuccess ? (
                <>
                  <p className="lp-section-kicker" style={{ color: '#10b981' }}>Próximo passo</p>
                  <h2 className="lp-section-title">Ative sua Creche Protegida</h2>
                  <p className="lp-section-sub">
                    Preencha o cadastro completo abaixo. Após a análise (em até 1 dia útil), você recebe
                    acesso liberado por <strong>7 dias gratuitos</strong>, sem cartão de crédito.
                  </p>

                  <form
                    className="lp-checkout-form"
                    onSubmit={async (e) => {
                      e.preventDefault();
                      setCheckoutError('');

                      if (checkoutData.senha.length < 12) {
                        setCheckoutError('A senha deve ter no mínimo 12 caracteres.');
                        return;
                      }
                      if (checkoutData.senha !== checkoutData.confirmarSenha) {
                        setCheckoutError('As senhas não conferem.');
                        return;
                      }

                      setLoading(true);
                      try {
                        await api.post('/saas/checkout/lead', {
                          admin_nome: checkoutData.responsavelNome,
                          admin_cpf: checkoutData.responsavelCpf,
                          admin_rg: checkoutData.responsavelRg,
                          admin_rg_uf: checkoutData.responsavelRgUf,
                          admin_email: checkoutData.responsavelEmail,
                          admin_senha: checkoutData.senha,
                          // Mesmo Responsável Legal do login — grava também na creche,
                          // que é a fonte usada nos contratos gerados pelo sistema.
                          representante_legal_nome: checkoutData.responsavelNome,
                          representante_legal_cpf: checkoutData.responsavelCpf,
                          representante_legal_rg: checkoutData.responsavelRg,
                          representante_legal_rg_uf: checkoutData.responsavelRgUf,
                          representante_legal_nacionalidade: checkoutData.responsavelNacionalidade,
                          representante_legal_estado_civil: checkoutData.responsavelEstadoCivil,
                          representante_legal_profissao: checkoutData.responsavelProfissao,
                          cnpj: checkoutData.cnpj,
                          creche_nome: checkoutData.razaoSocial,
                          nome_fantasia: checkoutData.nomeFantasia,
                          telefone: checkoutData.telefone,
                          cep: checkoutData.cep,
                          endereco: checkoutData.endereco,
                          numero: checkoutData.numero,
                          complemento: checkoutData.complemento,
                          bairro: checkoutData.bairro,
                          cidade: checkoutData.cidade,
                          estado: checkoutData.estado,
                          plano: checkoutData.plano,
                          metodo_pagamento: 'cartao',
                          // O preço NÃO vai daqui de propósito: quem diz o valor
                          // do plano é o servidor. Enquanto o número era montado
                          // no navegador, dava para assinar OURO por R$1 mexendo
                          // na requisição — e qualquer mudança de preço no site
                          // divergia calada da cobrança.
                        });
                        setCheckoutSuccess(true);
                      } catch (err: any) {
                        setCheckoutError(err?.response?.data?.message || 'Falha ao processar solicitação. Verifique os dados e tente novamente.');
                        console.error(err);
                      } finally {
                        setLoading(false);
                      }
                    }}
                  >
                    <CrecheCadastroFields
                      value={checkoutData}
                      onChange={setCheckoutData}
                      mostrarSenha
                      onBuscarCnpj={buscarCnpjLead}
                      buscandoCnpj={buscandoCnpjLead}
                      erroCnpj={erroCnpjLead}
                      onBuscarCep={buscarCepLead}
                      buscandoCep={buscandoCepLead}
                      disabled={loading}
                    />
                    {checkoutError && <div className="lp-form-error">{checkoutError}</div>}

                    <button
                      type="submit"
                      className="lp-cta-primary lp-cta-lg"
                      disabled={loading}
                      style={{ width: '100%', marginTop: '20px' }}
                    >
                      {loading ? 'Processando...' : 'Enviar Cadastro para Análise →'}
                    </button>
                    
                    <p style={{ fontSize: '12px', marginTop: '15px', opacity: 0.7 }}>
                      Ao clicar em finalizar, você aceita nossos termos de uso e política de privacidade. 100% LGPD Compliant.
                    </p>
                  </form>
                </>
              ) : (
                <div className="lp-checkout-success">
                  <div className="lp-success-icon">🎉</div>
                  <h2>Cadastro Recebido!</h2>
                  <p>
                    Recebemos o cadastro da sua creche e ele está <strong>em análise</strong> (aprovação em até 1 dia útil).
                    Assim que for aprovado, seu <strong>período de teste gratuito de 7 dias</strong> começa automaticamente.
                  </p>
                  <div className="lp-success-steps">
                    <p><strong>Como acessar quando for liberado:</strong></p>
                    <ol>
                      <li>Entre na página de <strong>Login</strong> do sistema.</li>
                      <li>Use o e-mail <strong>{checkoutData.responsavelEmail}</strong> e a <strong>senha que você acabou de cadastrar</strong>.</li>
                      <li>Você também receberá um e-mail confirmando a liberação — mas não precisa esperar por ele para tentar entrar.</li>
                    </ol>
                    <p className="lp-success-hint">
                      Se tentar entrar antes da aprovação, o sistema avisa que seu cadastro ainda está em análise —
                      é sinal de que deu tudo certo e o acesso será liberado em breve.
                    </p>
                  </div>
                  <a href="/login" className="lp-cta-primary lp-cta-lg" style={{ marginTop: '16px', display: 'inline-block' }}>
                    Ir para o Login →
                  </a>
                  <button onClick={() => setCheckoutSuccess(false)} className="lp-cta-secondary-dark" style={{ marginTop: '12px' }}>
                    Voltar
                  </button>
                </div>
              )}
            </div>

            <div className="lp-contact-pills" style={{ marginTop: '40px' }}>
              <span>📧 suporte@gestaocreche.com.br</span>
              <span>📞 (48) 9 9111-8686</span>
              <span>💬 WhatsApp disponível</span>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════ FOOTER ══════════════ */}
      <footer className="lp-footer">
        <div className="lp-container">
          <div className="lp-footer-card">
            
            <div className="lp-footer-top">
              <a href="#inicio" className="lp-logo">
                <span className="lp-logo-icon">🏫</span>
                <span className="lp-logo-text">Gestão<strong>Creches</strong></span>
              </a>
              <p>
                SaaS seguro para gestão moderna de creches e CEIs.
                Segurança LGPD garantida, vigilância integrada, Liz AI 24h e 15 módulos operacionais.
                Sua creche protegida, pais tranquilos, negócio crescendo.
              </p>
            </div>

            <div className="lp-footer-links">
              <div className="lp-footer-col">
                <h4>Produto</h4>
                <a href="#funcionalidades">Funcionalidades</a>
                <a href="#modulos">Módulos</a>
                <a href="#planos">Planos e preços</a>
                <Link to="/portal">Demo pública</Link>
              </div>

              <div className="lp-footer-col">
                <h4>Empresa</h4>
                <a href="#contato">Sobre a Alencastro</a>
                <a href="#contato">Contato</a>
                <a href="#contato">Suporte</a>
                <a href="#contato">Parcerias</a>
              </div>

              <div className="lp-footer-col">
                <h4>SaaS</h4>
                <Link to="/saas/login">Portal Admin SaaS</Link>
                <a href="#contato">Status Global</a>
                <a href="#contato">Documentação</a>
              </div>
            </div>

            <div className="lp-footer-bottom">
              <p>Desenvolvido e comercializado por <strong>Alencastro Consultoria</strong></p>
              <p>GestãoCreches é um produto da Alencastro Consultoria</p>
              <p>© {new Date().getFullYear()} Alencastro Consultoria. Todos os direitos reservados.</p>
            </div>

          </div>
        </div>
      </footer>

      <LizSalesChat open={lizChatOpen} onClose={() => setLizChatOpen(false)} />
    </div>
  );
}
